import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import * as _ from 'lodash';
import { Badge, BadgeColors, BadgeShapes } from '../../generic/Badge';
import DetailsPanel, { DetailsPanelList } from '../../generic/DetailsPanel';
import { Section } from '../../shared/vulns/Drawer';
import * as IssuesV2Store from '../../../redux/issuesV2';
import type { DetailsPanelListRowProps } from '../../generic/DetailsPanel/DetailsPanelListRow';
import type { VulnIssue } from '../../../../shared/issuesV2';
import styles from './IssueDetails.module.less';
import { fetcherToPlainText } from '../../../../shared/fetchers';
import type { Fetcher } from '../../../../shared/fetchers';
import Tooltip from '../../generic/Tooltip';
import { renderRevision } from '../../../../shared/helpers/renderLocator';

type DepthText =
  | `Direct (${number})`
  | `Transitive (${number})`
  | `Direct (${number}) and Transitive (${number})`;

/**
 * Determines the text for the depths section.
 *
 * @param {VulnIssue['depths']} depths - The various "depths" at which the dependency is used.
 */
const getDepthText = (
  depths: VulnIssue['depths'],
): DepthText => {
  if (depths.direct && depths.deep) {
    return `Direct (${depths.direct}) and Transitive (${depths.deep})`;
  }
  return depths.direct > depths.deep
    ? `Direct (${depths.direct})`
    : `Transitive (${depths.deep})`;
};

type Props = {
  additionalRows?: DetailsPanelListRowProps[];

  // Display the package correction message.
  isPackageCorrected?: boolean;
};

/**
 * IssueDetailsDependencySection displays the dependency info
 * for the package which flagged this vulnerability info.
 */
const IssueDetailsDependencySection: VoidFunctionComponent<Props> = ({
  additionalRows,
  isPackageCorrected,
}) => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnIssue;
  const containerLayer = useSelector(IssuesV2Store.selectors.getContainerLayer);

  if (!issueDetails) {
    return null;
  }

  const { source: { packageManager } } = issueDetails;

  const rows: DetailsPanelListRowProps[] = _.compact([
    {
      label: 'Package name',
      content: (
        <span className="font-family-monospace">
          {issueDetails.source.name}
        </span>
      ),
    },
    issueDetails.source.version && {
      label: 'Version',
      content: (
        <Badge
          color={BadgeColors.LightGray}
          shape={BadgeShapes.Circle}
        >
          {renderRevision(issueDetails.source.id)}
        </Badge>
      ),
    },
    issueDetails.cpes?.[0] && {
      label: 'CPE',
      content: (
        <span className="font-family-monospace">
          {issueDetails.cpes[0]}
        </span>
      ),
    },
    {
      label: 'Depth(s)',
      content: (
        <span className="font-family-monospace">
          {getDepthText(issueDetails.depths)}
        </span>
      ),
    },
    containerLayer && {
      label: 'Container layer(s)',
      content: (
        <span className="font-family-monospace">
          {containerLayer}
        </span>
      ),
    },
    {
      label: 'Locator',
      content: (
        <span className="font-family-monospace">
          {issueDetails.source.id}
        </span>
      ),
    },
    {
      label: 'Package manager',
      content: (
        <span className="font-family-monospace">
          {fetcherToPlainText(packageManager as Fetcher) || packageManager}
        </span>
      ),
    },
    issueDetails.source.url && {
      label: 'Homepage',
      content: (
        <Tooltip tooltip={issueDetails.source.url} id="source-url">
          <a href={issueDetails.source.url} target="_blank" rel="noreferrer">
            <span className={styles.homePageLink}>
              {issueDetails.source.url}
            </span>
          </a>
        </Tooltip>
      ),
    },
    ...(additionalRows || []),
  ]);

  return (
    <Section>
      <DetailsPanel
        id="issue-details-dependency-section"
        target="Dependency"
        loadingRowCount={rows.length}
      >
        <DetailsPanelList rows={rows} />
        {isPackageCorrected && (
          <div className={styles.packageCorrectionMessage}>This package has been manually corrected</div>
        )}
      </DetailsPanel>
    </Section>
  );
};

export default IssueDetailsDependencySection;
